import classNames from 'classnames';
import hoistNonReactStatics from 'hoist-non-react-statics';
import React from 'react';
import DescriptionsIcon from './components/descriptions-icon';
import DescriptionItem, { DescriptionItemProps } from './components/descriptions-item';
import DescriptionsLabel from './components/descriptions-label';
import DescriptionsPrefix from './components/descriptions-prefix';
import { DescriptionContextProvider } from './descriptions.context';
import DescriptionTable, { descriptionsClasses } from './descriptions.styled';
import { TDescriptionsLayout, TDescriptionsVariant } from './descriptions.types';

interface DescriptionsProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLTableElement>, HTMLTableElement> {
  variant?: TDescriptionsVariant;
  layout?: TDescriptionsLayout;
  items: (Omit<DescriptionItemProps, 'variant'> | null)[];
}

const Description = (
  { variant = 'on-light', layout = 'vertical', className, items, ...rest }: DescriptionsProps,
  ref?: React.Ref<HTMLTableElement>,
) => {
  const contextValue = React.useMemo(() => ({ variant, layout }), [variant, layout]);

  return (
    <DescriptionContextProvider value={contextValue}>
      <DescriptionTable
        {...rest}
        className={classNames(
          descriptionsClasses.component,
          className,
          `${descriptionsClasses.component}-${variant}`,
          `${descriptionsClasses.component}-${layout}`,
        )}
        ref={ref}
      >
        <tbody>
          {items.map((child, i) => {
            return child ? (
              <DescriptionItem left={child.left} right={child.right} layout={layout} key={i} />
            ) : null;
          })}
        </tbody>
      </DescriptionTable>
    </DescriptionContextProvider>
  );
};

Description.Item = DescriptionItem;
Description.Label = DescriptionsLabel;
Description.Icon = DescriptionsIcon;
Description.Prefix = DescriptionsPrefix;

export default hoistNonReactStatics(
  React.memo(React.forwardRef(Description)),
  Description,
) as typeof Description;
